import { useMutation } from '@tanstack/react-query';
import { observerUnCoursEnDirect } from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-20 — OBSERVER UN COURS EN DIRECT, ET POURQUOI CETTE SURFACE VIT DANS
 * `web/` ALORS QUE LE GESTE PART DE L'ADMINISTRATION.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **F-20 était la SEULE fonctionnalité du corpus qui n'était mentionnée nulle part dans
 * le code**, balayé le 20260829. La route existait, l'écran d'administration l'appelait,
 * et il n'affichait que deux compteurs : rien de ce que F-20 promet n'était servi.
 *
 * « **Ce qu'un observateur reçoit** : le tableau qui se remplit, **les curseurs nommés**,
 * les écrans partagés. **Jamais l'image ni le son** » (Q424, Q425, Q427).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **TROIS ISSUES, DEUX FERMÉES, ET LA DÉCISION EST JOURNALISÉE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Recevoir « le tableau qui se remplit » veut dire **peindre une scène**, donc **connaître
 * le moteur de tableau**. Trois façons de le faire, et deux sont fermées par une décision
 * écrite :
 *
 * 1. **Faire importer le moteur par `admin/`** — casse le port étroit de D-012, « le
 *    moteur doit être isolé derrière une interface étroite, **sinon le remplacement n'est
 *    plus un changement de moteur mais une réécriture du front de séance** », et fait
 *    rougir `web/e2e/port-du-moteur.spec.ts`, qui exige **un seul fichier importeur**.
 * 2. **Dupliquer le port dans `admin/`** — crée une seconde vérité, et « deux copies d'un
 *    même gabarit divergent à la première modification ». `admin/` et `web/` « ne se
 *    parlent jamais entre eux : aucun import direct, **aucun composant partagé** ».
 * 3. **Ouvrir la surface dans `web/`, et y renvoyer par URL depuis `admin/`.** Retenue.
 *    **Un renvoi par URL n'est pas un import** : les deux surfaces ne partagent aucun
 *    code, chacune garde sa configuration, et le port reste unique.
 *
 * C'est une **modalité d'implémentation**, pas un changement de périmètre — F-20 est déjà
 * spécifiée, et rien de ce qu'elle accorde ni de ce qu'elle refuse ne bouge.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA BORNE DE Q567 N'EST PAS REJOUÉE ICI, ET C'EST LE POINT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Une structure n'observe qu'un cours dont **un de ses élèves** est présent » (Q288), et
 * « **cette borne est déclarée sur la route qui porte le geste** (Q567), et **le geste n'a
 * plus qu'une route** (Q566) — une borne d'autorisation qui dépend du chemin emprunté est
 * une faille par construction ».
 *
 * Cette page appelle donc **la même route que l'écran d'administration**, et c'est elle
 * qui autorise. Le canal, de son côté, ne rejoue pas la borne : il vérifie **que le geste
 * autorisé a eu lieu** — une ligne d'observation ouverte. Rien n'est décidé deux fois.
 *
 * **L'appel est idempotent** : « une ligne ouverte pour ce couple observateur-cours est la
 * même observation, vue d'un second onglet ». Ouvrir la page depuis le renvoi de `admin/`
 * ne consomme donc pas une seconde ligne, et l'ouvrir directement fonctionne.
 */

/**
 * **Ouvre — ou retrouve — l'observation d'un cours, et rend ce qu'elle autorise.**
 *
 * `signalee` suit **le rôle** : « le passage d'un `orgadmin` est signalé par un bandeau ;
 * celui du compte qui voit tout ne l'est pas, **son passage restant constatable par sa
 * trace** » (Q213, Q214). L'écran le dit à l'observateur, parce que savoir qu'on est vu
 * change ce qu'on fait — et parce que **rien d'autre ne le lui dirait**.
 */
export function useObservation(coursId: string) {
  return useMutation({
    mutationFn: () =>
      deballer(observerUnCoursEnDirect({ path: { id: coursId } })),
  });
}
